import React from 'react'
import './user-role-list.scss'
import { classnames } from '@halo/_utils/classnames'
import { observer } from 'mobx-react'
import {
    Loading3QuartersOutlined,
    WarningOutlined,
    EyeOutlined,
    QuestionCircleOutlined,
    EyeInvisibleOutlined,
} from '@ant-design/icons'
import { Table, Input, Modal, Form, FormInstance, Flex } from 'antd'
import { Button } from '@pushly/aqe/lib/components'
import type { ButtonAction } from '@pushly/aqe/lib/components'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { TablePaginationConfig } from 'antd/es/table'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/es/table/interface'
import { RuleObject } from 'antd/es/form'
import { AppState } from '@halo/stores/app.state'
import { IUserRole } from '@halo/interfaces/user-role'
import UserRoleService from '@halo/services/user-role.service'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import TableRowActiveBadge from '@halo/components/table-row-active-badge/table-row-active-badge'
import { isAxiosResponseError } from '@halo/_utils/axios'
import { simpleNotification } from '@halo/_utils/notifications'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'

const DISABLE_KEY = 'DISABLE'

interface IListProps {}

interface IListState {
    loading: boolean
    rolesLoaded: boolean
    allRoles: IUserRole[]
    visibleRoles: IUserRole[]
    tableMeta: any
    sortedInfo: any
    page: number
    pageSize: number
    query?: string
    paginationLinks?: IApiResponsePaginationLinks
}

@observer
class UserRoleList extends React.Component<IListProps, IListState> {
    protected appState: AppState
    protected appService: AppService
    protected userRoleService: UserRoleService

    public constructor(props: IListProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.userRoleService = Container.get(UserRoleService)

        this.state = {
            loading: true,
            rolesLoaded: false,
            allRoles: [],
            visibleRoles: [],
            tableMeta: {},
            page: 1,
            pageSize: 15,
            sortedInfo: {
                order: 'ascend',
                columnKey: 'name',
            },
        }
    }

    public componentDidMount(): void {
        this.ensureUserRolesLoaded()
    }

    public async ensureUserRolesLoaded() {
        await this.fetchUserRoles()
    }

    public render() {
        const tableHeader = (
            <Flex className="user-role-list-header" gap={8} justify="space-between" align="center">
                <Flex flex={1} gap={8} align="center">
                    <Input.Search
                        className="user-role-list-search"
                        style={{ width: 300 }}
                        size="middle"
                        placeholder="Search by ID or Role Name"
                        enterButton={true}
                        onSearch={this.searchUserRoles}
                    />
                </Flex>
            </Flex>
        )

        return (
            <div className={classnames('user-role-list')}>
                <Table<IUserRole>
                    className="aqe-table-well"
                    title={() => tableHeader}
                    size="small"
                    bordered={false}
                    dataSource={this.state.visibleRoles}
                    rowKey={(row) => row.id}
                    onChange={this.handleChange}
                    pagination={false}
                    footer={(currentTableData) => (
                        <SwSimpleLinkPagination
                            currentTableData={currentTableData}
                            links={this.state.paginationLinks}
                            onPrev={(_ev, _link, config) => {
                                this.setState({ page: config.current! }, () => this.fetchUserRoles())
                            }}
                            onNext={(_ev, _link, config) => {
                                this.setState({ page: config.current! }, () => this.fetchUserRoles())
                            }}
                        />
                    )}
                    loading={
                        this.isLoading && {
                            indicator: <Loading3QuartersOutlined spin={true} />,
                        }
                    }
                >
                    <Table.Column className="role" title="Role" dataIndex="name" render={this.renderDisplay} />
                    <Table.Column
                        className="active"
                        title="Enabled"
                        dataIndex="is_active"
                        align="center"
                        render={TableRowActiveBadge}
                    />
                    <Table.Column
                        className="actions"
                        title="Actions"
                        align="center"
                        render={(_, role: IUserRole) => this.renderRowActions(role)}
                    />
                </Table>
            </div>
        )
    }

    protected get isLoading(): boolean {
        return this.state.loading && !this.state.rolesLoaded
    }

    protected renderDisplay = (text: string, role: IUserRole): React.ReactNode => {
        const name = role.description ?? titleCase(role.name)

        return <TableRowEntityDisplay title={name} status={role.name} />
    }

    protected renderRowActions(role: IUserRole): React.ReactNode {
        const actions: ButtonAction[] = []

        if (role.is_active) {
            actions.push({
                key: 'disable',
                text: 'Disable',
                icon: <EyeInvisibleOutlined />,
                onClick: () => this.handleActionClick('disable', role, null as any),
            })
        } else {
            actions.push({
                key: 'enable',
                text: 'Enable',
                icon: <EyeOutlined />,
                onClick: () => this.handleActionClick('enable', role, null as any),
            })
        }

        return (
            <Button
                size="small"
                altHref={this.buildViewRoleUrl(role)}
                onClick={(ev) => this.handlePrimaryActionClick(role, ev as React.MouseEvent<HTMLElement>)}
                actions={actions.length > 0 ? actions : undefined}
            >
                View
            </Button>
        )
    }

    protected buildViewRoleUrl(role: IUserRole): string {
        return `/access-control/user-roles/${role.id}`
    }

    protected handleChange = (
        _pagination: TablePaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter: SorterResult<IUserRole>,
        _extra: TableCurrentDataSource<IUserRole>,
    ) => {
        this.setState({ sortedInfo: sorter })
        this.fetchUserRoles()
    }

    protected handlePrimaryActionClick(role: IUserRole, event: React.MouseEvent<HTMLElement>): void {
        if (event.metaKey) {
            window.open(this.buildViewRoleUrl(role))
        } else {
            event.preventDefault()
            this.appService.route(this.buildViewRoleUrl(role))
        }
    }

    protected handleActionClick = (
        key: string | number,
        role: IUserRole,
        _domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ): void => {
        if (key === 'disable') {
            const formRef = React.createRef<FormInstance>()

            Modal.confirm({
                title: 'Disable User Role',
                icon: <WarningOutlined />,
                okText: 'Disable',
                onOk: async () => {
                    const form = formRef.current

                    if (form) {
                        await form.validateFields()
                        return this.handleDisable(role)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>
                            Are you sure you want to disable this user role? Any users currently assigned this role will
                            lose access to the platform.
                        </p>

                        <p>
                            Please type, <b>{DISABLE_KEY}</b>, to continue with this action.
                        </p>

                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule: RuleObject, value, callback) => {
                                        if (!value?.trim()) {
                                            callback(`${DISABLE_KEY} keyword is required`)
                                        } else if (value !== 'DISABLE') {
                                            callback(`You must type ${DISABLE_KEY} to continue`)
                                        } else {
                                            callback()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else if (key === 'enable') {
            Modal.confirm({
                title: 'Enable Org',
                icon: <QuestionCircleOutlined />,
                okText: 'Enable',
                onOk: () => this.handleEnable(role),
                content: (
                    <p>
                        Are you sure you want to enable this user role? Any users currently assigned this role will be
                        re-enabled and have access to the platform again.
                    </p>
                ),
            } as any)
        }
    }

    protected handleDisable = async (role: IUserRole) => {
        const res = await this.userRoleService.patch(role.id, {
            is_active: false,
        })

        if (res.ok) {
            await this.fetchUserRoles()
        } else if (isAxiosResponseError(res.error)) {
            simpleNotification('error', res.error.response.data.message ?? res.error.message)
        }
    }

    protected handleEnable = async (role: IUserRole) => {
        const res = await this.userRoleService.patch(role.id, {
            is_active: true,
        })

        if (res.ok) {
            await this.fetchUserRoles()
        } else if (isAxiosResponseError(res.error)) {
            simpleNotification('error', res.error.response.data.message ?? res.error.message)
        }
    }

    protected searchUserRoles = async (data: string | any) => {
        const value = typeof data !== 'string' ? data.target.value : data

        if (!value || value.length === 0 || value.length >= 2) {
            await this.setState({ page: 1, query: value })
            await this.fetchUserRoles(value)
        }
    }

    protected async fetchUserRoles(search?: string) {
        this.setState({ loading: true, rolesLoaded: false })
        const update: Partial<IListState> = {
            loading: false,
        }

        try {
            const { data, meta } =
                (await this.userRoleService.fetchAll({
                    query: {
                        limit: this.state.pageSize,
                        page: this.state.page,
                        search: search ? search : this.state.query,
                        pagination: 1,
                    },
                    showLoadingScreen: false,
                })) || {}

            const userRoles: IUserRole[] = data.map((role) => {
                return {
                    id: role.id,
                    user_type_id: role.user_type_id,
                    name: role.name,
                    description: role.description ?? null,
                    is_active: role.is_active,
                    has_network_scope: role.has_network_scope,
                    created_at: role.created_at,
                }
            })

            update.rolesLoaded = true
            update.allRoles = userRoles
            update.visibleRoles = userRoles
            update.tableMeta = meta
            update.paginationLinks = meta.links ?? update.paginationLinks
        } catch (error) {
            console.warn(error)
        }

        this.setState(update as IListState)
    }
}

export default UserRoleList
